import { useEffect, useRef, useState } from "react";
import { Box, Paper, Skeleton, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MapsEmissionsNodesType } from "../..";
import { Co2Icon, WarehouseIcon } from "../../../../../../../assets/icons";
import GraphMap from "../../../../../../../components/charts/GraphMap";
import { HeatLayer, MarkerLayer } from "../../../../../../../components/charts/GraphMap/layers";
import ScaleLegend from "../../../../../../../components/charts/GraphMap/legends/ScaleLegend";
import PaperCard from "../../../../../../../components/widgets/PaperCard";
import PaperCardHeader from "../../../../../../../components/widgets/PaperCard/PaperCardHeader";
import { useCurrentTheme } from "../../../../../../../theme/theme";
import { ThemeColors } from "../../../../../../../theme/themeValues";
import { MapBounds } from "../../../../useOperationalUnitsData";

interface Props {
	maps: MapsEmissionsNodesType;
	onBoundsChange: (bounds: MapBounds) => void;
	mapBounds?: MapBounds;
}

type MapType = "emissions" | "distribution";

const MapEmissionNodes = ({ maps, onBoundsChange, mapBounds }: Props) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const { t } = useTranslation("feature.units");
	const { theme } = useCurrentTheme();

	// Stable data states to prevent map reloads on data change
	// Used to show data only when the query is completed
	const [stableNodesData, setStableNodesData] = useState(maps.nodes.nodesData);
	const [stableEmissionData, setStableEmissionData] = useState(maps.emissions.emissionData);

	// State for map type (emissions or distribution)
	const [map, setMap] = useState<MapType>("emissions");

	// Icon colors based on selected map
	const iconStyleLeft = {
		color: map === "emissions" ? theme.palette.primary.contrastText : ThemeColors.LightSecondary,
	};
	const iconStyleRight = {
		color: map === "distribution" ? theme.palette.primary.contrastText : ThemeColors.LightSecondary,
	};

	useEffect(() => {
		if (!maps.nodes.isLoading && maps.nodes.nodesData) {
			setStableNodesData(maps.nodes.nodesData);
		}
	}, [maps.nodes.isLoading, maps.nodes.nodesData]);

	useEffect(() => {
		if (!maps.emissions.isLoading && maps.emissions.emissionData) {
			setStableEmissionData(maps.emissions.emissionData);
		}
	}, [maps.emissions.isLoading, maps.emissions.emissionData]);

	// Handler for toggle button change
	const handleChange = (_event: React.MouseEvent<HTMLElement>, newMap: MapType | null) => {
		if (newMap !== null) {
			setMap(newMap);
		}
	};

	// Toggle button style
	const toggleStyle = {
		backgroundColor: ThemeColors.LightBackgroundElevation0,
		"&:hover": { backgroundColor: ThemeColors.LightBackground },
		"&.Mui-selected": {
			backgroundColor: ThemeColors.LightSecondary,
			"&:hover": { backgroundColor: ThemeColors.LightSecondaryLight },
		},
	};

	// Toggle button children
	const children = [
		<ToggleButton value="emissions" key="emissions" sx={toggleStyle}>
			<Co2Icon sx={iconStyleLeft} />
		</ToggleButton>,
		<ToggleButton value="distribution" key="distribution" sx={toggleStyle}>
			<WarehouseIcon sx={iconStyleRight} />
		</ToggleButton>,
	];

	// Tile provider for map (draw or heremaps)
	const titleProvider = map === "emissions" ? "draw" : "heremaps";

	// Map layer based on selected map
	const mapLayer =
		map === "emissions" ? (
			<HeatLayer
				color={ThemeColors.LightPrimary}
				data={stableEmissionData?.data}
				scale={stableEmissionData?.scale}
			/>
		) : (
			stableNodesData && <MarkerLayer nodes={stableNodesData.items} color={ThemeColors.LightPrimary} />
		);

	// Show skeleton while loading
	// Show loding of all the box only on emissions map, which is the first shown
	// For the other map the loading is manage in different way
	if (maps.emissions.isLoading) {
		return <Skeleton variant="rounded" height="100%" width="100%" />;
	}

	const isMapLoading = map === "emissions" ? maps.emissions.isLoading : maps.nodes.isLoading;

	return (
		<PaperCard
			ref={containerRef}
			header={
				<PaperCardHeader
					containerRef={containerRef}
					title={t("greenrouter/distribution of emissions and operating units")}
					isLoading={isMapLoading}
					onReload={maps.nodes.reload}
				/>
			}
			content={
				<Stack height="100%" width="100%" px={2}>
					<Paper elevation={3} sx={{ width: "100%", height: "100%", position: "relative" }}>
						{/* Main map */}
						<GraphMap
							tileProvider={titleProvider}
							isLoading={isMapLoading}
							zoom={3}
							maxZoom={20}
							minZoom={1}
							bounds={mapBounds}
							onBoundsChange={onBoundsChange}
						>
							{mapLayer}
						</GraphMap>

						{/* Toggle buttons for map type */}
						<ToggleButtonGroup
							value={map}
							onChange={handleChange}
							exclusive
							sx={{ position: "absolute", bottom: 16, right: 8, zIndex: 1000 }}
							size="small"
							aria-label="Map type"
						>
							{children}
						</ToggleButtonGroup>
					</Paper>

					{/* Scale legend only for emissions map */}
					{map === "emissions" && (
						<Box pt={1}>
							<ScaleLegend
								title="tC02"
								scale={maps.emissions.emissionData?.scale}
								baseColor={ThemeColors.LightPrimary}
							/>
						</Box>
					)}
				</Stack>
			}
			sxContent={{ paddingRight: 0, paddingLeft: 0 }}
		/>
	);
};

export default MapEmissionNodes;
